// The TI-108 as a pure state machine.
//
// The TSIA2 offers a TI-108 (and a variant with a square-root key) on the
// items College Board flags for a four-function calculator. This module is
// the calculator's behaviour with no React and no DOM: a state, a key set,
// and one function that applies a key. The component renders it; the test
// file drives it directly.
//
// The key set is exactly what the real screen shows (reference screenshots
// 16 and 30): the ten digits, the point, the four operations, equals, ON/C,
// the three memory keys, and on the variant a square-root key. Nothing else.
//
// Behaviour follows the TI-108 itself:
//   - no operator precedence: 2 + 3 × 4 = is 20, as on any four-function
//     calculator;
//   - an eight-digit display, trailing zeros dropped;
//   - ON/C pressed once clears the entry, pressed twice in a row clears the
//     pending calculation as well (memory is separate);
//   - MRC recalls memory; pressed twice in a row it clears memory;
//   - M+ and M- add the display to memory or subtract it, and the next digit
//     starts a new entry;
//   - dividing by zero shows Error until the next clear or digit.

export type TI108Op = '+' | '-' | '×' | '÷';

export type TI108Key =
    | '0' | '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9' | '.'
    | TI108Op | '=' | 'ON/C' | 'MRC' | 'M+' | 'M-' | '√';

export interface TI108State {
    /** What the display shows: digits, or 'Error'. */
    display: string;
    /** Left operand once an operator has been pressed. */
    accumulator: number | null;
    /** The operator waiting for its right operand. */
    pending: TI108Op | null;
    /** True while the next digit should start a new number rather than append. */
    startNewEntry: boolean;
    memory: number;
    /** Last key pressed, for the double-press meanings of ON/C and MRC. */
    lastKey: TI108Key | null;
    /** The operation equals last performed, so equals again repeats it
     *  (the TI-108's automatic constant: 2 + 3 = = is 8). */
    constant: { op: TI108Op; operand: number } | null;
}

export const DISPLAY_DIGITS = 8;

export const initialState: TI108State = {
    display: '0',
    accumulator: null,
    pending: null,
    startNewEntry: true,
    memory: 0,
    lastKey: null,
    constant: null,
};

const isDigit = (key: TI108Key): key is Exclude<TI108Key, TI108Op | '=' | 'ON/C' | 'MRC' | 'M+' | 'M-' | '√'> =>
    /^[0-9.]$/.test(key);

const isOp = (key: TI108Key): key is TI108Op =>
    key === '+' || key === '-' || key === '×' || key === '÷';

/** The number on the display, 0 for Error. */
export const displayValue = (state: TI108State): number => {
    const value = parseFloat(state.display);
    return Number.isFinite(value) ? value : 0;
};

/** Format a result the way the eight-digit display would show it.
 *
 * Eight digit CELLS, not eight significant digits: the leading 0 of a
 * fraction takes a cell, so 2 ÷ 3 shows 0.6666667, not 0.66666667. Trailing
 * zeros are dropped. A whole part wider than eight digits is an overflow
 * error, as on the real calculator.
 */
export const formatDisplay = (value: number): string => {
    if (!Number.isFinite(value)) return 'Error';
    const magnitude = Math.abs(value);
    const wholeDigits = magnitude < 1 ? 1 : Math.floor(Math.log10(magnitude)) + 1;
    if (wholeDigits > DISPLAY_DIGITS) return 'Error';
    const decimals = DISPLAY_DIGITS - wholeDigits;
    // Rounding can carry into a new whole digit (9.9999999 -> 10.000000);
    // re-check the width on the rounded value.
    const rounded = Number(value.toFixed(decimals));
    if (Math.abs(rounded) >= 10 ** DISPLAY_DIGITS) return 'Error';
    const fixed = rounded.toFixed(decimals);
    // Trailing zeros are dropped only after a point; in a whole number they
    // are the number.
    const text = fixed.includes('.') ? fixed.replace(/\.?0+$/, '') : fixed;
    return text === '' || text === '-0' ? '0' : text;
};

const apply = (op: TI108Op, left: number, right: number): number => {
    switch (op) {
        case '+': return left + right;
        case '-': return left - right;
        case '×': return left * right;
        case '÷': return right === 0 ? NaN : left / right;
        default: return right;
    }
};

const withKey = (state: TI108State, key: TI108Key): TI108State => ({ ...state, lastKey: key });

/** Apply one key press. Pure: returns the next state, never mutates. */
export function press(state: TI108State, key: TI108Key): TI108State {
    // ON/C: once clears the entry; twice in a row clears the calculation too.
    if (key === 'ON/C') {
        const clearAll = state.lastKey === 'ON/C' || state.display === 'Error';
        return withKey({
            ...state,
            display: '0',
            startNewEntry: true,
            accumulator: clearAll ? null : state.accumulator,
            pending: clearAll ? null : state.pending,
            constant: clearAll ? null : state.constant,
        }, key);
    }

    // After Error, only ON/C or a fresh digit gets the calculator back.
    if (state.display === 'Error' && !isDigit(key)) {
        return withKey(state, key);
    }

    if (isDigit(key)) {
        if (state.startNewEntry || state.display === 'Error') {
            return withKey({
                ...state,
                display: key === '.' ? '0.' : key,
                startNewEntry: false,
            }, key);
        }
        if (key === '.' && state.display.includes('.')) return withKey(state, key);
        if (state.display.replace('-', '').replace('.', '').length >= DISPLAY_DIGITS) {
            return withKey(state, key);
        }
        const display = state.display === '0' && key !== '.' ? key : state.display + key;
        return withKey({ ...state, display }, key);
    }

    if (isOp(key)) {
        // Pressing a second operator before a number swaps the operator.
        if (state.pending !== null && state.startNewEntry && state.lastKey !== null && isOp(state.lastKey)) {
            return withKey({ ...state, pending: key }, key);
        }
        const result = state.pending !== null && state.accumulator !== null
            ? apply(state.pending, state.accumulator, displayValue(state))
            : displayValue(state);
        const display = formatDisplay(result);
        return withKey({
            ...state,
            display,
            accumulator: display === 'Error' ? null : result,
            pending: display === 'Error' ? null : key,
            startNewEntry: true,
        }, key);
    }

    if (key === '=') {
        if (state.pending === null || state.accumulator === null) {
            // No operation waiting: repeat the last one on the display
            // (automatic constant), or do nothing if there was none.
            if (state.constant === null) {
                return withKey({ ...state, startNewEntry: true }, key);
            }
            const repeated = apply(state.constant.op, displayValue(state), state.constant.operand);
            return withKey({ ...state, display: formatDisplay(repeated), startNewEntry: true }, key);
        }
        const operand = displayValue(state);
        const result = apply(state.pending, state.accumulator, operand);
        const display = formatDisplay(result);
        return withKey({
            ...state,
            display,
            accumulator: null,
            pending: null,
            startNewEntry: true,
            // A calculation that ended in Error (divide by zero, overflow)
            // leaves no constant to repeat.
            constant: display === 'Error' ? null : { op: state.pending, operand },
        }, key);
    }

    if (key === '√') {
        const value = displayValue(state);
        return withKey({
            ...state,
            display: value < 0 ? 'Error' : formatDisplay(Math.sqrt(value)),
            startNewEntry: true,
        }, key);
    }

    if (key === 'M+' || key === 'M-') {
        const delta = displayValue(state);
        return withKey({
            ...state,
            memory: key === 'M+' ? state.memory + delta : state.memory - delta,
            startNewEntry: true,
        }, key);
    }

    if (key === 'MRC') {
        // Second press in a row clears memory; the display keeps the value.
        if (state.lastKey === 'MRC') {
            return withKey({ ...state, memory: 0 }, key);
        }
        return withKey({
            ...state,
            display: formatDisplay(state.memory),
            startNewEntry: true,
        }, key);
    }

    return state;
}

/** Apply a sequence of keys from the initial state. */
export const run = (keys: TI108Key[], from: TI108State = initialState): TI108State =>
    keys.reduce(press, from);

// --- which calculators an item offers -------------------------------------

export type CalculatorMode = 'basic' | 'sqrt' | 'graphing';
export type CalculatorKind = CalculatorMode;

/** Cumulative: an item's flag offers everything up to and including it. */
export const OFFERED: Record<CalculatorMode, CalculatorKind[]> = {
    basic: ['basic'],
    sqrt: ['basic', 'sqrt'],
    graphing: ['basic', 'sqrt', 'graphing'],
};
